import React, { useState } from 'react';
import { Trash2, Eye, Database, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useCompatibilityCache, CacheEntry } from '@/hooks/admin/useCompatibilityCache';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const CacheEntryDetails: React.FC<{ entry: CacheEntry }> = ({ entry }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="bg-white/5 rounded-lg p-3">
        <div className="text-xs text-white/60 mb-1">Score</div>
        <div className="text-2xl font-bold text-luxury-pinkBright">{entry.compatibility_score}%</div>
      </div>
      <div className="bg-white/5 rounded-lg p-3">
        <div className="text-xs text-white/60 mb-1">Modelo</div>
        <div className="text-lg font-medium text-white">{entry.ai_model || 'N/A'}</div>
      </div>
    </div>

    {entry.ai_summary && (
      <div className="bg-white/5 rounded-lg p-3">
        <div className="text-xs text-white/60 mb-2">Resumo IA</div>
        <p className="text-white text-sm">{entry.ai_summary}</p>
      </div>
    )}

    {entry.common_points && entry.common_points.length > 0 && (
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
        <div className="text-xs text-green-400 mb-2">Pontos em comum</div>
        <ul className="space-y-1">
          {entry.common_points.map((point, i) => (
            <li key={i} className="text-white text-sm flex items-start gap-2">
              <span className="text-green-400">•</span>
              {point}
            </li>
          ))}
        </ul>
      </div>
    )}

    {entry.differences && entry.differences.length > 0 && (
      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3">
        <div className="text-xs text-orange-400 mb-2">Diferenças</div>
        <ul className="space-y-1">
          {entry.differences.map((diff, i) => (
            <li key={i} className="text-white text-sm flex items-start gap-2">
              <span className="text-orange-400">•</span>
              {diff}
            </li>
          ))}
        </ul>
      </div>
    )}

    <div className="bg-white/5 rounded-lg p-3">
      <div className="text-xs text-white/60 mb-2">JSON Raw</div>
      <pre className="text-xs text-gray-300 whitespace-pre-wrap break-words bg-black/30 p-3 rounded-lg overflow-x-auto max-h-48">
        {JSON.stringify(entry, null, 2)}
      </pre>
    </div>
  </div>
);

export const CompatibilityCacheManager: React.FC = () => {
  const { entries, stats, isLoading, isDeleting, fetchEntries, deleteEntry, clearAllCache } = useCompatibilityCache();
  const [selectedEntry, setSelectedEntry] = useState<CacheEntry | null>(null);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="text-xs text-white/60">Total</div>
            <div className="text-2xl font-bold text-white">{stats.total}</div>
          </CardContent>
        </Card>
        
        {Object.entries(stats.byModel).map(([model, count]) => (
          <Card key={model} className="bg-white/5 border-white/10">
            <CardContent className="p-4">
              <div className="text-xs text-white/60 truncate">{model}</div>
              <div className="text-2xl font-bold text-luxury-pinkBright">{count}</div>
            </CardContent>
          </Card>
        ))}

        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-4">
            <div className="text-xs text-white/60">Mais recente</div>
            <div className="text-sm font-medium text-white">
              {stats.mostRecent 
                ? formatDistanceToNow(new Date(stats.mostRecent), { addSuffix: true, locale: ptBR })
                : 'N/A'
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={fetchEntries}
          disabled={isLoading}
          variant="outline"
          className="border-white/20 text-white hover:bg-white/10"
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Atualizar
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="destructive"
              disabled={isDeleting || entries.length === 0}
              className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Limpar Todo Cache
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-gray-900 border-white/20">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Limpar todo o cache?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">
                Esta ação irá remover todas as {stats.total} entradas de compatibilidade do cache.
                Os próximos cálculos serão feitos novamente pela IA.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/10 text-white border-white/20 hover:bg-white/20">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={clearAllCache}
                className="bg-red-500 hover:bg-red-600"
              >
                Limpar Tudo
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Entries Table */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Database className="h-5 w-5" />
            Entradas do Cache ({entries.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-luxury-pinkBright" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              Nenhuma entrada no cache
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-2 text-xs text-white/60 font-medium">Usuário 1</th>
                    <th className="text-left py-3 px-2 text-xs text-white/60 font-medium">Usuário 2</th>
                    <th className="text-center py-3 px-2 text-xs text-white/60 font-medium">Score</th>
                    <th className="text-left py-3 px-2 text-xs text-white/60 font-medium">Data</th>
                    <th className="text-right py-3 px-2 text-xs text-white/60 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-2">
                        <span className="text-white text-sm">{entry.user1_username || 'N/A'}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-white text-sm">{entry.user2_username || 'N/A'}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="text-luxury-pinkBright font-bold">{entry.compatibility_score}%</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-white/60 text-xs">
                          {entry.created_at 
                            ? formatDistanceToNow(new Date(entry.created_at), { addSuffix: true, locale: ptBR })
                            : 'N/A'
                          }
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10"
                                onClick={() => setSelectedEntry(entry)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-gray-900 border-white/20 max-w-2xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle className="text-white">
                                  {entry.user1_username} ↔ {entry.user2_username}
                                </DialogTitle>
                              </DialogHeader>
                              <CacheEntryDetails entry={entry} />
                            </DialogContent>
                          </Dialog>

                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => deleteEntry(entry.id)}
                            disabled={isDeleting}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
